import { CircleStop, Crown, DoorOpen, Hand, Mic, MicOff, MonitorX, PencilLine, UserX } from 'lucide-react';
import { useState, type RefObject } from 'react';
import { LIMITS, type Participant } from '../../shared/protocol';
import { Button, MenuItem, Modal, Popover } from '../components/ui';
import type { MeetingSession } from '../meeting/MeetingSession';

/** Host actions on someone else, or self actions (rename, hand) on our own entry. */
export function ParticipantMenu({
  session,
  participant: p,
  isSelf,
  anchor,
  onClose,
  placement = 'top',
}: {
  session: MeetingSession;
  participant: Participant;
  isSelf: boolean;
  anchor: RefObject<HTMLElement | null>;
  onClose: () => void;
  placement?: 'top' | 'bottom';
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [rename, setRename] = useState(false);
  const amHost = session.isHost;
  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  if (confirmRemove) {
    return (
      <Modal
        title={`¿Retirar a ${p.name}?`}
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={run(() => session.host({ action: 'remove', target: p.id }))}>
              Retirar
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>{p.name} saldrá de la reunión y no podrá volver a entrar en esta sesión.</p>
      </Modal>
    );
  }
  if (rename) return <RenameDialog current={p.name} onClose={onClose} onSave={(name) => session.rename(name)} />;

  return (
    <Popover anchor={anchor} onClose={onClose} placement={placement} label={`Opciones de ${p.name}`}>
      {isSelf ? (
        <>
          <MenuItem icon={<PencilLine size={16} />} onSelect={() => setRename(true)} disabled={!amHost && !session.store.get().roomSettings.allowRename}>
            Cambiar nombre
          </MenuItem>
          <MenuItem icon={<Hand size={16} />} onSelect={run(() => session.setHandRaised(!p.handRaised))}>
            {p.handRaised ? 'Bajar la mano' : 'Levantar la mano'}
          </MenuItem>
        </>
      ) : (
        amHost && (
          <>
            {p.audioMuted ? (
              <MenuItem icon={<Mic size={16} />} onSelect={run(() => session.host({ action: 'ask-unmute', target: p.id }))}>
                Pedir que active el audio
              </MenuItem>
            ) : (
              <MenuItem icon={<MicOff size={16} />} onSelect={run(() => session.host({ action: 'mute', target: p.id }))}>
                Silenciar
              </MenuItem>
            )}
            {p.handRaised && (
              <MenuItem icon={<Hand size={16} />} onSelect={run(() => session.host({ action: 'lower-hand', target: p.id }))}>
                Bajar la mano
              </MenuItem>
            )}
            {p.sharing && (
              <MenuItem icon={<MonitorX size={16} />} onSelect={run(() => session.host({ action: 'stop-share', target: p.id }))}>
                Detener su pantalla compartida
              </MenuItem>
            )}
            {p.recording && (
              <MenuItem icon={<CircleStop size={16} />} onSelect={run(() => session.host({ action: 'stop-recording', target: p.id }))}>
                Detener su grabación
              </MenuItem>
            )}
            <MenuItem icon={<Crown size={16} />} onSelect={run(() => session.host({ action: 'make-host', target: p.id }))}>
              Hacer anfitrión
            </MenuItem>
            <MenuItem icon={<DoorOpen size={16} />} onSelect={run(() => session.host({ action: 'to-waiting', target: p.id }))}>
              Poner en la sala de espera
            </MenuItem>
            <div className="menu-sep" />
            <MenuItem icon={<UserX size={16} />} danger onSelect={() => setConfirmRemove(true)}>
              Retirar de la reunión
            </MenuItem>
          </>
        )
      )}
    </Popover>
  );
}

export function RenameDialog({ current, onClose, onSave }: { current: string; onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState(current);
  const valid = name.trim().length > 0 && name.trim() !== current;
  return (
    <Modal
      title="Cambiar nombre"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="rename-form" disabled={!valid}>
            Guardar
          </Button>
        </>
      }
    >
      <form
        id="rename-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          onSave(name.trim());
          onClose();
        }}
      >
        <label className="field">
          <span>Nuevo nombre</span>
          <input className="input" value={name} maxLength={LIMITS.nameLength} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
      </form>
    </Modal>
  );
}
